import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useClientForm, useClientFormAction, useDeleteClientForm } from '../../api/queries';
import { useToast } from '../../components/Toast';
import { ErrorState, PageHeader, ProgressBar, Spinner, StatusBadge } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import type { ClientFormDetail } from '../../lib/types';
import { formatDateTime } from '../../lib/utils';
import { AnswersView } from './AnswersView';
import { SharePanel } from './SharePanel';

type Detail = { clientForm: ClientFormDetail };

export function LinkDetailPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: cf, isPending, error } = useClientForm(id);

  const setActive = useClientFormAction(id, (isActive: boolean) =>
    api.patch<Detail>(`/client-forms/${id}`, { isActive }),
  );
  const reopen = useClientFormAction(id, () => api.post<Detail>(`/client-forms/${id}/reopen`));
  const sync = useClientFormAction(id, () => api.post<Detail>(`/client-forms/${id}/sync-template`));
  const remove = useDeleteClientForm();

  if (isPending) return <Spinner />;
  if (error)
    return (
      <ErrorState
        message={errorMessage(error)}
        action={<Link to="/admin/links">Back to client links</Link>}
      />
    );

  const run = (promise: Promise<unknown>, success: string) =>
    promise
      .then(() => toast.success(success))
      .catch((err: unknown) => toast.error(errorMessage(err)));

  function handleDelete() {
    if (
      !window.confirm(
        `Delete the link for ${cf!.clientName}? Their answers and attachments will be permanently deleted.`,
      )
    )
      return;
    remove.mutate(id, {
      onSuccess: () => {
        toast.success('Link deleted');
        navigate('/admin/links');
      },
      onError: (err) => toast.error(errorMessage(err)),
    });
  }

  return (
    <>
      <div className="no-print">
        <Link to="/admin/links" className="btn btn-ghost btn-sm">
          ← Client links
        </Link>
      </div>

      <PageHeader
        title={
          <>
            {cf.clientName} <StatusBadge status={cf.status} isActive={cf.isActive} />
          </>
        }
        subtitle={cf.templateName}
        actions={
          <div className="row wrap no-print">
            <button className="btn btn-secondary btn-sm" onClick={() => window.print()}>
              Print / PDF
            </button>
            {cf.status === 'submitted' ? (
              <button
                className="btn btn-secondary btn-sm"
                onClick={() =>
                  window.confirm('Allow the client to edit and submit again?') &&
                  void run(reopen.mutateAsync(undefined), 'Form reopened for editing')
                }
              >
                Reopen for editing
              </button>
            ) : (
              <button
                className="btn btn-secondary btn-sm"
                title="Apply the latest version of the template to this link"
                onClick={() =>
                  window.confirm(
                    'Update this link to the latest version of the template? Answers to removed questions will be dropped.',
                  ) && void run(sync.mutateAsync(undefined), 'Updated to the latest template')
                }
              >
                Update to latest template
              </button>
            )}
            <button
              className="btn btn-secondary btn-sm"
              onClick={() =>
                void run(
                  setActive.mutateAsync(!cf.isActive),
                  cf.isActive ? 'Link disabled' : 'Link enabled',
                )
              }
            >
              {cf.isActive ? 'Disable link' : 'Enable link'}
            </button>
            <button className="btn btn-ghost btn-sm danger" onClick={handleDelete}>
              Delete
            </button>
          </div>
        }
      />

      {params.get('created') && (
        <div className="alert alert-success no-print">
          Link created. Copy it or send it to {cf.clientName} below.
        </div>
      )}

      <div className="detail-grid">
        <div className="no-print">
          <SharePanel clientForm={cf} />
        </div>
        <section className="card stats">
          <h2>Progress</h2>
          <ProgressBar value={cf.answeredCount} max={cf.questionCount} />
          <p>
            <strong>{cf.answeredCount}</strong> of {cf.questionCount} questions answered
          </p>
          <dl className="meta">
            <dt>Created</dt>
            <dd>{formatDateTime(cf.createdAt)}</dd>
            <dt>First opened</dt>
            <dd>{formatDateTime(cf.firstOpenedAt)}</dd>
            <dt>Last saved</dt>
            <dd>{formatDateTime(cf.lastSavedAt)}</dd>
            <dt>Submitted</dt>
            <dd>{formatDateTime(cf.submittedAt)}</dd>
          </dl>
        </section>
      </div>

      <h2 className="answers-title">Answers</h2>
      {cf.status === 'pending' ? (
        <p className="muted">{cf.clientName} has not started the form yet.</p>
      ) : (
        <AnswersView form={cf.form} answers={cf.answers} />
      )}
    </>
  );
}
